// Derives the "sources" list for column 2 of the 3-column UI.
//
// A Source is one of:
//   - "all-packages"  : pinned synthetic row at top, contains every decision
//   - "blog-post"     : one Socket/StepSec post that went through the classifier
//                       (may or may not have been extracted from, depending
//                       on the verdict)
//   - "ghsa-bulk"     : synthetic group for every package sourced from GHSA
//   - "osv-bulk"      : synthetic group for every package sourced from OSV
//
// Linkage from a decision back to its source:
//   - blog-post: match decision.sourceItemUrl against the classifier's
//     data.sourceUrl
//   - ghsa-bulk: decision.sources includes "ghsa-malware" (or any source
//     prefix "ghsa-")
//   - osv-bulk: decision.sources includes "osv" (or any source containing
//     "osv")
//   - all-packages: always includes every decision
//
// A package can appear under multiple sources (e.g. art-template was caught
// by both Socket blog and ghsa-malware in one run). That's intentional —
// the source view is "what each source independently surfaced", not a
// dedup count.

import type { Decision, LogEventBase, RunSummary } from "./types";

export type SourceKind = "all-packages" | "blog-post" | "ghsa-bulk" | "osv-bulk";

export type Source = {
  id: string;
  kind: SourceKind;

  // Display fields
  title: string;
  url: string | null; // null for synthetic groups

  // Blog-post-only fields. Both null/undefined for synthetic groups.
  classifierCategory?: string | null;
  classifierRationale?: string | null;
  feedKey?: string | null;
  extractedCount?: number | null;
  extractionTruncated?: boolean;
  extractionParseError?: string | null;
  classifierTimestamp?: string | null;

  // Packages attributed to this source
  decisions: Decision[];

  // Convenience counters (computed)
  decisionCount: number;
  actionableCount: number; // block / review / error / skip
};

// Decide whether a decision belongs to a blog-post source by URL match.
function decisionBelongsToBlog(d: Decision, sourceUrl: string): boolean {
  if (!d.sourceItemUrl) return false;
  // Tolerate trailing slash + query-string differences. socket.dev's feed
  // sometimes adds ?utm_medium=feed; the classifier's logged sourceUrl may
  // include or omit it, and decision.sourceItemUrl may have its own variant.
  return canonicalUrl(d.sourceItemUrl) === canonicalUrl(sourceUrl);
}

function canonicalUrl(u: string): string {
  try {
    const url = new URL(u);
    url.search = ""; // strip query string
    url.hash = "";
    return url.toString().replace(/\/$/, "");
  } catch {
    return u;
  }
}

function decisionBelongsToGhsa(d: Decision): boolean {
  return d.sources.some((s) => s.startsWith("ghsa"));
}

function decisionBelongsToOsv(d: Decision): boolean {
  // OSV decisions are tagged "osv" today, but some early runs used the per-
  // ecosystem subkey. Substring match is good enough.
  return d.sources.some((s) => s.includes("osv"));
}

// Join classifier + extractor events by sourceUrl (each post has at most
// one of each in a given run). Returns one Source per post that the
// classifier ran on.
function buildBlogPostSources(
  classifications: LogEventBase[],
  extractions: LogEventBase[],
  decisions: Decision[],
): Source[] {
  const extractionsByUrl = new Map<string, LogEventBase>();
  for (const e of extractions) {
    const url = (e.data.sourceUrl as string | undefined) ?? null;
    if (url) extractionsByUrl.set(canonicalUrl(url), e);
  }

  const out: Source[] = [];
  for (const c of classifications) {
    const url = (c.data.sourceUrl as string | undefined) ?? null;
    if (!url) continue;
    const canonical = canonicalUrl(url);
    const extraction = extractionsByUrl.get(canonical);
    const title =
      (c.data.sourceTitle as string | undefined) ?? deriveTitleFromUrl(url);
    const linked = decisions.filter((d) => decisionBelongsToBlog(d, url));
    out.push({
      id: `blog:${canonical}`,
      kind: "blog-post",
      title,
      url,
      classifierCategory: (c.data.category as string | undefined) ?? null,
      classifierRationale: (c.data.rationale as string | undefined) ?? null,
      feedKey: (c.data.feedKey as string | undefined) ?? null,
      classifierTimestamp: c.timestamp,
      extractedCount: extraction
        ? ((extraction.data.extractedCount as number | undefined) ?? 0)
        : null,
      extractionTruncated: extraction?.data.truncated === true,
      extractionParseError:
        (extraction?.data.parseError as string | null | undefined) ?? null,
      decisions: linked,
      decisionCount: linked.length,
      actionableCount: countActionable(linked),
    });
  }
  return out;
}

function deriveTitleFromUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/\/$/, "");
    const last = path.split("/").filter(Boolean).pop() ?? u.host;
    return last.replace(/-/g, " ");
  } catch {
    return url;
  }
}

function countActionable(decisions: Decision[]): number {
  let n = 0;
  for (const d of decisions) {
    if (d.action === "block" || d.action === "review" || d.action === "error" || d.action === "skip") n++;
  }
  return n;
}

// Public entry point — builds the sources list for a run, in a stable
// display order:
//   1. "all-packages" (pinned, always present)
//   2. blog posts (compromise_alert first, then others, alphabetical by title)
//   3. GHSA bulk (when any GHSA-sourced decisions exist)
//   4. OSV bulk (when any OSV-sourced decisions exist)
export function buildSources(run: RunSummary): Source[] {
  const allDecisions = run.decisions;

  const allPackages: Source = {
    id: "all-packages",
    kind: "all-packages",
    title: "All packages",
    url: null,
    decisions: allDecisions,
    decisionCount: allDecisions.length,
    actionableCount: countActionable(allDecisions),
  };

  const blogPosts = buildBlogPostSources(
    run.classifications,
    run.extractions,
    allDecisions,
  ).sort((a, b) => {
    // compromise_alert posts first — those are the ones likely to need
    // attention. Then alphabetical by title.
    const aAlert = a.classifierCategory === "compromise_alert" ? 0 : 1;
    const bAlert = b.classifierCategory === "compromise_alert" ? 0 : 1;
    if (aAlert !== bAlert) return aAlert - bAlert;
    return a.title.localeCompare(b.title);
  });

  const ghsaDecisions = allDecisions.filter(decisionBelongsToGhsa);
  const ghsa: Source | null =
    ghsaDecisions.length > 0
      ? {
          id: "ghsa-bulk",
          kind: "ghsa-bulk",
          title: "GHSA — bulk advisories",
          url: null,
          decisions: ghsaDecisions,
          decisionCount: ghsaDecisions.length,
          actionableCount: countActionable(ghsaDecisions),
        }
      : null;

  const osvDecisions = allDecisions.filter(decisionBelongsToOsv);
  const osv: Source | null =
    osvDecisions.length > 0
      ? {
          id: "osv-bulk",
          kind: "osv-bulk",
          title: "OSV — bulk advisories",
          url: null,
          decisions: osvDecisions,
          decisionCount: osvDecisions.length,
          actionableCount: countActionable(osvDecisions),
        }
      : null;

  const sources: Source[] = [allPackages, ...blogPosts];
  if (ghsa) sources.push(ghsa);
  if (osv) sources.push(osv);
  return sources;
}

// Convenience: explain WHY a source has zero packages, for column 3 empty
// state. Returns null if there's no special reason (just "no packages").
export function explainEmptySource(source: Source): string | null {
  if (source.decisionCount > 0) return null;
  if (source.kind !== "blog-post") return null;
  if (source.extractionParseError) {
    return `Claude's extraction response failed to parse: ${source.extractionParseError}`;
  }
  if (source.extractionTruncated) {
    return "Claude hit max_tokens during extraction — packages may be missing. Check audit log for the raw response.";
  }
  if (source.classifierCategory && source.classifierCategory !== "compromise_alert") {
    return `Classifier verdict was "${source.classifierCategory}" — extractor was skipped. Rationale: ${source.classifierRationale ?? "(none)"}`;
  }
  if (source.extractedCount === 0) {
    return "Classifier flagged this as a compromise but the extractor returned 0 packages. Likely an unsupported ecosystem (GitHub Actions, browser extensions, etc.) — see post for context.";
  }
  return "No packages from this source in this run.";
}
